import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapPin, Building2, Briefcase, Calendar, ExternalLink, ArrowLeft, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import axios from 'axios';
import { toast } from 'sonner';
import { formatPublicationDate } from '@/components/JobCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetchJobDetails();
  }, [id]);

  const fetchJobDetails = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${id}`);
      setJob(response.data);
      setNotFound(false);
    } catch (error) {
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error('Erreur lors du chargement de l\'offre');
        navigate('/jobs');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500"></div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-slate-50 py-12 px-4">
        <div className="max-w-2xl mx-auto">
          <Card className="p-10 text-center" data-testid="job-not-found-card">
            <div className="mx-auto h-20 w-20 rounded-full bg-amber-100 flex items-center justify-center mb-6">
              <Briefcase className="h-10 w-10 text-amber-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 mb-3" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Cette offre n'est plus disponible
            </h1>
            <p className="text-slate-600 mb-2">
              L'offre que vous recherchez a été retirée, archivée ou n'existe plus.
            </p>
            <p className="text-sm text-slate-500 mb-8">
              Référence : <code className="bg-slate-100 px-2 py-1 rounded text-xs">{id}</code>
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={() => navigate('/jobs')}
                className="bg-sky-500 hover:bg-sky-600"
                data-testid="see-all-jobs-button"
              >
                <Briefcase className="h-4 w-4 mr-2" />
                Voir toutes les offres actuelles
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                data-testid="back-home-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Retour à l'accueil
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-8">
              💡 Astuce : Inscrivez-vous gratuitement pour recevoir des alertes sur les nouvelles offres correspondant à votre profil.
            </p>
          </Card>
        </div>
      </div>
    );
  }

  if (!job) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/jobs')}
          className="mb-6"
          data-testid="back-to-jobs-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour aux offres
        </Button>

        {/* Job Header Card */}
        <Card className="p-8 mb-6 bg-white" data-testid="job-details-card">
          <div className="flex items-start justify-between mb-6 gap-4">
            <div className="shrink-0 h-20 w-20 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-center overflow-hidden">
              <img
                src={job.logo || '/mosala-logo-square.jpg'}
                alt={job.entreprise}
                className="h-full w-full object-contain"
                onError={(e) => { e.target.src = '/mosala-logo-square.jpg'; }}
                data-testid="job-details-logo"
              />
            </div>
            <div className="flex-1 min-w-0">
              <h1 
                className="text-3xl md:text-4xl font-bold text-slate-900 mb-3 tracking-tight" 
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                data-testid="job-title"
              >
                {job.titre}
              </h1>
              <div className="flex items-center text-lg text-slate-700 mb-4">
                <Building2 className="h-5 w-5 mr-2" />
                <span className="font-medium">{job.entreprise}</span>
              </div>
            </div>
            <Badge className="bg-sky-100 text-sky-700 hover:bg-sky-100 text-lg px-4 py-2 shrink-0">
              {job.domaine}
            </Badge>
          </div>

          {/* Job Info Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="flex items-center text-slate-600">
              <MapPin className="h-5 w-5 mr-3 text-sky-500" />
              <div>
                <div className="text-xs text-slate-500">Localisation</div>
                <div className="font-medium">{job.localisation}</div>
              </div>
            </div>
            <div className="flex items-center text-slate-600">
              <Briefcase className="h-5 w-5 mr-3 text-sky-500" />
              <div>
                <div className="text-xs text-slate-500">Type de contrat</div>
                <div className="font-medium">{job.type_contrat}</div>
              </div>
            </div>
            {job.salaire && (
              <div className="flex items-center text-slate-600">
                <DollarSign className="h-5 w-5 mr-3 text-sky-500" />
                <div>
                  <div className="text-xs text-slate-500">Salaire</div>
                  <div className="font-medium">{job.salaire}</div>
                </div>
              </div>
            )}
            <div className="flex items-center text-slate-600">
              <Calendar className="h-5 w-5 mr-3 text-sky-500" />
              <div>
                <div className="text-xs text-slate-500">Date de publication</div>
                <div className="font-medium" data-testid="job-publication-date">{formatPublicationDate(job)}</div>
              </div>
            </div>
          </div>

          {/* Source Badge */}
          <div className="flex items-center gap-2 mb-6">
            <span className="text-sm text-slate-500">Source:</span>
            <Badge variant="outline">{job.source}</Badge>
          </div>

          {/* Apply Button */}
          <div className="flex gap-4">
            <Button 
              className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 py-6 text-lg font-semibold shadow-lg shadow-sky-500/20"
              onClick={() => {
                if (job.url) {
                  window.open(job.url, '_blank');
                } else {
                  toast.info('Lien de candidature non disponible');
                }
              }}
              data-testid="apply-button"
            >
              Postuler maintenant
              <ExternalLink className="h-5 w-5 ml-2" />
            </Button>
          </div>
        </Card>

        {/* Job Description Card */}
        <Card className="p-8 bg-white" data-testid="job-description-card">
          <h2 
            className="text-2xl font-bold text-slate-900 mb-4" 
            style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
          >
            Description du poste
          </h2>
          <div className="prose prose-slate max-w-none">
            <p className="text-slate-700 leading-relaxed whitespace-pre-line">
              {job.description}
            </p>
          </div>
        </Card>

        {/* CTA Card */}
        <Card className="p-8 mt-6 bg-gradient-to-br from-sky-50 to-white border-sky-100">
          <div className="text-center">
            <h3 className="text-xl font-bold text-slate-900 mb-2">Recevez des offres similaires</h3>
            <p className="text-slate-600 mb-4">
              Inscrivez-vous pour recevoir des notifications pour les emplois qui correspondent à votre profil.
            </p>
            <Button 
              onClick={() => navigate('/auth')}
              className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-6"
              data-testid="signup-cta-button"
            >
              S'inscrire gratuitement
            </Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default JobDetails;
